import {
  faEyeSlash,
  faEye,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react-lite";
import { useState, FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ErrorAlert } from "../shared/components/alert/Alert";
import ErrorBoundary from "../shared/components/error-boundary/ErrorBoundary";
import { LoadingEllipsis } from "../shared/components/loading/Loading";
import DisabledAccount from "./DisabledAccount";
import { useAppContext } from "../shared/functions/Context";

const Loader = () => (
  <div className="flex justify-center items-center h-screen">
    <LoadingEllipsis />
  </div>
);

type ILocationState = { from: string };

const LoggedOut: React.FC = observer(() => {
  const { api, store } = useAppContext();
  const location = useLocation();

  const [signInForm, setSignInForm] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [loggingloading, setLogginLoading] = useState(false);
  const [userNotFoundError, setUserNotFoundError] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);

  const togglePassword = () => {
    setPasswordType((prev) => (prev === "password" ? "text" : "password"));
  };

  const onSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLogginLoading(true);
    const { email, password } = signInForm;

    const $user = await api.auth.signIn(email, password);
    if (!$user) {
      setUserNotFoundError(true);
      setLogginLoading(false);
      return;
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    await api.auth.sendResetPasswordEmail(resetEmail);
    alert("Reset link sent!");
    setIsForgotPasswordMode(false);
  };

  if (store.auth.loading) return <Loader />;
  if (store.auth.me && store.auth.me.asJson.disabled)
    return <DisabledAccount title="Disabled" />;

  if (!store.auth.loading && store.auth.me) {
    const state = location.state as ILocationState;
    return <Navigate to={state?.from ?? "/c/home/dashboard"} />;
  }


return (
  <ErrorBoundary>
    <div className="relative isolate min-h-screen flex items-center justify-center overflow-hidden bg-gray-950">

      {/* BACKGROUND IMAGE */}
      <img
        alt=""
        src="https://images.unsplash.com/photo-1508385082359-f38ae991e8f2?auto=format&fit=crop&q=80&w=2400"
        className="absolute inset-0 -z-10 w-full h-full object-cover opacity-30"
      />

      {/* DARK NIGHT BLUR OVERLAY */}
      <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm -z-10" />

      {/* CARD */}
      <div className="backdrop-blur-xl bg-gray-900/60 shadow-2xl rounded-2xl w-full max-w-5xl border border-gray-700 overflow-hidden flex">

        {/* LEFT SIDE */}
        <div className="hidden md:flex flex-col justify-center p-12 w-1/2 
                        bg-gradient-to-br from-neutral-900/80 to-neutral-950/90 border-r border-gray-800">
          <h2 className="text-4xl font-bold text-white">E-Performance Management System</h2>
          <p className="mt-4 text-gray-300 text-lg">
            Performance Management is an ongoing engagement and communication
            process involving supervisors and employees.
          </p>
        </div>

        {/* RIGHT SIDE */}
        <div className="w-full md:w-1/2 p-10 text-white">

          {userNotFoundError && (
            <ErrorAlert
              msg="Username or password is incorrect"
              onClose={() => setUserNotFoundError(false)}
            />
          )}

          {!isForgotPasswordMode ? (
            <>
              <h3 className="text-3xl font-bold mb-2 text-white">Login</h3>
              <p className="mb-6 text-gray-400">Enter your details to access your dashboard.</p>

              <form onSubmit={onSignIn} className="space-y-6">

                {/* EMAIL */}
                <div>
                  <label className="block mb-1 font-medium text-gray-300">Email Address</label>
                  <div className="flex items-center bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-3 
                                  focus-within:border-gray-600 focus-within:ring-0">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 mr-3" />
                    <input
                      type="email"
                      value={signInForm.email}
                      onChange={(e) => setSignInForm({ ...signInForm, email: e.target.value })}
                      className="w-full bg-transparent text-white placeholder-gray-500 outline-none"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                {/* PASSWORD */}
                <div>
                  <label className="block mb-1 font-medium text-gray-300">Password</label>
                  <div className="flex items-center bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-3 
                                  focus-within:border-gray-600 focus-within:ring-0">
                    <input
                      type={passwordType}
                      value={signInForm.password}
                      onChange={(e) => setSignInForm({ ...signInForm, password: e.target.value })}
                      className="w-full bg-transparent text-white placeholder-gray-500 outline-none"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={togglePassword}
                      className="text-gray-400 hover:text-white"
                    >
                      <FontAwesomeIcon icon={passwordType === "password" ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </div>

                {/* LOGIN BUTTON */}
                <div className="flex flex-col space-y-4 mt-6">
                  <button
                    type="submit"
                    className="w-full bg-neutral-900 hover:bg-black text-white py-3 rounded-lg 
                               font-semibold text-lg transition-all border border-gray-700 hover:border-gray-500"
                  >
                    Login
                    {loggingloading && <span className="ml-2 animate-pulse">...</span>}
                  </button>

                  <button
                    type="button"
                    className="text-gray-400 hover:text-white hover:underline"
                    onClick={() => setIsForgotPasswordMode(true)}
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h3 className="text-3xl font-bold mb-2 text-white">Reset Password</h3>
              <p className="mb-6 text-gray-400">Enter your email to receive a reset link.</p>

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label className="block mb-1 font-medium text-gray-300">Email Address</label>
                  <div className="flex items-center bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-3 
                                  focus-within:border-gray-600 focus-within:ring-0">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 mr-3" />
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full bg-transparent text-white placeholder-gray-500 outline-none"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col space-y-4 mt-6">
                  <button
                    type="submit"
                    className="w-full bg-neutral-900 hover:bg-black text-white py-3 rounded-lg 
                               font-semibold text-lg transition-all border border-gray-700 hover:border-gray-500"
                  >
                    Send Reset Link
                  </button>

                  <button
                    type="button"
                    className="text-gray-400 hover:text-white hover:underline"
                    onClick={() => setIsForgotPasswordMode(false)}
                  >
                    Return to Login
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  </ErrorBoundary>
);



});

export default LoggedOut;
